import { useCallback, useEffect, useRef } from "react";
import { listChatMonitors } from "@/lib/api";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

const POLL_INTERVAL_MS = 5_000;

/**
 * 現在のチャットのモニター一覧をストアと同期する。WorkspacePanels から一度だけマウントする。
 * モニターのステータス変化はサーバから push されないため、チャット切替時・ターン境界・
 * monitorEvent 到着時・実行中 (streaming または Active モニターあり) の定期ポーリングで追従する。
 */
export function useChatMonitorsSync(): void {
	const chatId = useChatStore((s) => s.chatId);
	const status = useChatStore((s) => s.status);
	const hasActiveMonitor = useChatStore((s) =>
		(s.monitors ?? []).some((m) => m.status === "active"),
	);
	const monitorEventCount = useChatStore((s) =>
		s.messages.reduce((n, m) => (m.role === "monitorEvent" ? n + 1 : n), 0),
	);

	const syncStateRef = useRef({ requested: 0, applied: 0, errorShown: false });
	const fetchMonitors = useCallback(async (targetChatId: string) => {
		const state = syncStateRef.current;
		const seq = ++state.requested;
		try {
			const monitors = await listChatMonitors(targetChatId);
			if (useChatStore.getState().chatId !== targetChatId || seq <= state.applied) return;
			state.applied = seq;
			state.errorShown = false;
			useChatStore.getState().setMonitors(monitors);
		} catch (err: unknown) {
			console.error("モニター一覧の取得に失敗しました", err);
			if (useChatStore.getState().chatId !== targetChatId || seq <= state.applied) return;
			if (!state.errorShown) {
				state.errorShown = true;
				useToastStore.getState().show({
					type: "error",
					message: "モニター一覧を更新できませんでした。表示は前回取得時の状態です。",
				});
			}
		}
	}, []);

	// 同じチャットへ戻った場合も、前回開いていた時の未完了リクエストは反映しない。
	useEffect(() => {
		const state = syncStateRef.current;
		state.errorShown = false;
		return () => {
			state.applied = ++state.requested;
		};
	}, [chatId]);

	useEffect(() => {
		if (chatId !== null) void fetchMonitors(chatId);
	}, [chatId, status, fetchMonitors]);

	// monitorEvent 到着時: drain が走った=状態が動いた合図として再取得する。
	useEffect(() => {
		if (chatId === null || monitorEventCount === 0) return;
		void fetchMonitors(chatId);
	}, [chatId, monitorEventCount, fetchMonitors]);

	// 実行中は定期ポーリングで Active→終了の遷移やターン中の start_monitor を拾う。
	useEffect(() => {
		if (chatId === null) return;
		if (status !== "streaming" && !hasActiveMonitor) return;
		const timer = setInterval(() => {
			void fetchMonitors(chatId);
		}, POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [chatId, status, hasActiveMonitor, fetchMonitors]);
}
